import * as React from "react";
import { Redirect, Route } from "react-router-dom";

import Audit from "./crewApp/pages/audit/Audit";
import { Documents } from "./crewApp/pages/documents/Documents";
import Locations from "./crewApp/pages/locationSearch/Locations";
import Logout from "./components/Logout";
import { LowInventoryList } from "./managerApp/resources/inventory/LowInventoryList";
import { Maps } from "./crewApp/pages/yardMap/Maps";
import { InventorySearch } from "./crewApp/pages/inventorySearch/InventorySearch";
import NetworkError from "./components/NetworkError";
import ProcessOutboundShipments from "./crewApp/pages/outboundShipments/ProcessOutboundShipments";
import { ProcessShipments } from "./crewApp/pages/inboundShipments/ProcessShipments";
import { ProcessWorkOrders } from "./crewApp/pages/workOrder/ProcessShipments";
import { TrackLocationContainer } from "./crewApp/pages/trackManagement/TrackLocation";
import { ScheduleShow } from "./managerApp/resources/reloadYard/scheduling/ScheduleShow";
import { YardUtilization } from "./crewApp/pages/utilization/YardUtilization";
import { TrackManagementAdmin } from "./managerApp/resources/trackCars/TrackManagementAdmin";
import { InboundList as SITList } from "./managerApp/resources/trackCars/SITList";
import { SITList as CustomerSITList } from "./customerApp/resources/inbounds/SITList";
import { Tasks } from "./crewApp/pages/myTasks/MyTasks";
import { OutboundDemurrageList } from "./managerApp/reports/shipment/OutboundDemurrageList";
import { isAuthorized } from "./utils/appUtils";
import { sessionYardRunsDriverCheckIn } from "./utils/gateAccess";
import { hasTracks } from "./utils/locationUtils";
import { AclType, UserType } from "./utils/constants";

import Reports from "./managerApp/reports/Reports";
import AvgInventoryTurnsByAccount from "./managerApp/reports/inventory/AvgInventoryTurnsByAccount";
import AvgInventoryTurnsByMonth from "./managerApp/reports/inventory/AvgInventoryTurnsByMonth";
import AvgInboundCarsByAccount from "./managerApp/reports/shipment/AvgInboundCarsByAccount";
import { InboundDemurrageList } from "./managerApp/reports/shipment/InboundDemurrageList";
import { InvoiceAuditReport } from "./managerApp/reports/financial/InvoiceAuditReport";
import { InventoryActivityReport } from "./managerApp/reports/inventory/InventoryActivityReport";
import { InventorySnapshotReport } from "./managerApp/reports/inventory/InventorySnapshotReport";
import { InventoryStorageReport } from "./managerApp/reports/inventory/InventoryStorageReport";
import { InventoryRecallReport } from "./managerApp/reports/inventory/InventoryRecallReport";
import InventoryByLoadType from "./managerApp/reports/inventory/InventoryByLoadType";
import { TotalInOutReport } from "./managerApp/reports/inventory/TotalInOutReport";
import { OpenOrdersReport } from "./managerApp/reports/shipment/OpenOrdersReport";
import { RailcarsProcessedReport } from "./managerApp/reports/shipment/RailcarsProcessedReport";
import { BillingDetailsReport } from "./managerApp/reports/financial/BillingDetailsReport";
import DailyInboundsChart from "./managerApp/reports/shipment/DailyInboundsChart";
import DailyOutboundsChart from "./managerApp/reports/shipment/DailyOutboundsChart";
import { DailyRevenueChart } from "./managerApp/reports/financial/DailyRevenueChart";
import { ProductActivityReport } from "./managerApp/reports/inventory/ProductActivityReport";
import { ProductVelocityReport } from "./managerApp/reports/inventory/ProductVelocityReport";
import { ClaimLoadPage } from "./carrierApp/resources/loadSearch/ClaimLoadPage";
import { GateQueuePage } from "./managerApp/resources/gateCheckIns/GateQueuePage";

const RedirectToZapierPanel = () => {
  const reloadYardId = window.appUser?.reloadYard?.id;
  const isAllowed = isAuthorized(AclType.MY_YARD);

  return isAllowed && reloadYardId ? (
    <Redirect to={`/reloadYards/${reloadYardId}/settings?t=7`} />
  ) : (
    <Redirect to="/" />
  );
};

const Routes = [
  <Route path="/mytasks" render={(props) => <Tasks {...props} />} />,
  <Route exact path="/logout" render={() => <Logout />} />,
  <Route path="/processAudit" render={(props) => <Audit {...props} />} />,
  <Route
    path="/processShipments"
    render={(props) => <ProcessShipments {...props} />}
  />,
  <Route
    path="/processWorkOrders"
    render={(props) => <ProcessWorkOrders {...props} />}
  />,
  <Route
    path="/processOutboundShipments"
    render={(props) => <ProcessOutboundShipments {...props} />}
  />,
  <Route path="/search" render={(props) => <InventorySearch {...props} />} />,
  <Route path="/locations" render={(props) => <Locations {...props} />} />,
  <Route path="/schedule" render={(props) => <ScheduleShow {...props} />} />,
  // Gate Management is a granted permission, so the route checks it too — a
  // supervisor without it must not reach the queue by typing the URL. Managers
  // pass every permission, but not even they have a queue at a yard that
  // checks trucks in at the office.
  <Route
    path="/gateQueue"
    render={(props) =>
      isAuthorized(AclType.GATE_MANAGEMENT) &&
      sessionYardRunsDriverCheckIn() ? (
        <GateQueuePage {...props} />
      ) : (
        <Redirect to="/" />
      )
    }
  />,
  // More specific report routes must come before "/reports" in this list
  // (react-admin renders customRoutes in a Switch).
  <Route
    path="/reports/product-velocity"
    render={(props) => <ProductVelocityReport {...props} />}
  />,
  <Route path="/reports" render={(props) => <Reports {...props} />} />,
  <Route
    path="/reportInventoryByLoadType"
    render={(props) => <InventoryByLoadType {...props} />}
  />,
  <Route
    path="/reportTurnoverByMonth"
    render={(props) => <AvgInventoryTurnsByMonth {...props} />}
  />,
  <Route
    path="/reportTurnoverByAccount"
    render={(props) => <AvgInventoryTurnsByAccount {...props} />}
  />,
  <Route path="/zapierApiKey" component={RedirectToZapierPanel} />,
  <Route
    path="/reportCarsByAccount"
    render={(props) => <AvgInboundCarsByAccount {...props} />}
  />,
  <Route
    path="/reportDailyInbounds"
    render={(props) => <DailyInboundsChart {...props} />}
  />,
  <Route
    path="/reportDailyOutbounds"
    render={(props) => <DailyOutboundsChart {...props} />}
  />,
  <Route
    path="/reportDailyRevenue"
    render={(props) => <DailyRevenueChart {...props} />}
  />,
  <Route
    path="/reportInboundDemurrage"
    render={(props) => (
      <InboundDemurrageList
        resource="inboundShipments"
        basePath="/inboundShipments"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportOutboundDemurrage"
    render={(props) => (
      <OutboundDemurrageList
        resource="outboundShipments"
        basePath="/outboundShipments"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportLowInventory"
    render={(props) => (
      <LowInventoryList
        resource="inventories"
        basePath="/inventories"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportInventoryActivity"
    render={(props) => (
      <InventoryActivityReport
        resource="inventories"
        basePath="/inventories"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportInventorySnapshot"
    render={(props) => (
      <InventorySnapshotReport
        resource="inventories"
        basePath="/inventories"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportProductActivity"
    render={(props) => (
      <ProductActivityReport
        resource="inventories"
        basePath="/inventories"
        {...props}
      />
    )}
  />,

  <Route
    path="/reportInvoiceAudit"
    render={(props) => (
      <InvoiceAuditReport resource="invoices" basePath="/invoices" {...props} />
    )}
  />,
  <Route
    path="/reportBillingDetails"
    render={(props) => (
      <BillingDetailsReport
        resource="invoiceServices"
        basePath="/invoiceServices"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportOpenOrders"
    render={(props) => (
      <OpenOrdersReport
        resource="inboundShipments"
        basePath="/inboundShipments"
        {...props}
      />
    )}
  />,

  <Route
    path="/reportInventoryStorage"
    render={(props) => (
      <InventoryStorageReport
        resource="inventories"
        basePath="/inventories"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportInventoryRecall"
    render={(props) => (
      <InventoryRecallReport
        resource="inventories"
        basePath="/inventories"
        {...props}
      />
    )}
  />,
  <Route
    path="/reportTotalInOut"
    render={(props) => <TotalInOutReport {...props} />}
  />,
  <Route
    path="/reportRailcarsProcessed"
    render={(props) => {
      // Manager portal only for now. These routes are shared with the
      // customer and carrier portals, and the report reads the whole yard.
      const t = window.appUser?.type;
      if (
        t !== UserType.MANAGER &&
        t !== UserType.SUPERVISOR &&
        t !== UserType.ADMIN
      ) {
        return <Redirect to="/" />;
      }
      return <RailcarsProcessedReport {...props} />;
    }}
  />,
  <Route path="/maps" render={(props) => <Maps {...props} />} />,
  <Route
    path="/utilization"
    render={(props) => <YardUtilization {...props} />}
  />,
  <Route
    path="/trackManagement"
    render={(props) => {
      if (!hasTracks()) {
        return <Redirect to="/" />;
      }
      const isCustomer = window.appUser?.type === UserType.CUSTOMER;
      if (isCustomer) {
        if (!isAuthorized(AclType.TRACK_MANAGEMENT)) {
          return <Redirect to="/" />;
        }
        return <TrackManagementAdmin {...props} readonly={true} />;
      }
      return <TrackLocationContainer {...props} />;
    }}
  />,
  <Route
    path="/trackManagementAdmin"
    render={(props) => <TrackManagementAdmin {...props} />}
  />,
  <Route
    path="/sitList"
    render={(props) =>
      window.appUser?.type === UserType.CUSTOMER ? (
        isAuthorized(AclType.SIT) ? (
          <CustomerSITList
            resource="inboundShipments"
            basePath="/inboundShipments"
            {...props}
          />
        ) : (
          <Redirect to="/" />
        )
      ) : (
        <SITList
          resource="inboundShipments"
          basePath="/inboundShipments"
          {...props}
        />
      )
    }
  />,
  <Route path="/documents" render={(props) => <Documents {...props} />} />,
  <Route
    path="/claimLoad"
    render={(props) => {
      const t = window.appUser?.type;
      if (t !== UserType.CARRIER_DISPATCH && t !== UserType.CARRIER_DRIVER) {
        return <Redirect to="/" />;
      }
      return <ClaimLoadPage {...props} />;
    }}
  />,
  <Route
    path="/networkError"
    render={(props) => <NetworkError {...props} />}
  />,
];

export default Routes;
